import React, { useState } from 'react';
import { MessageSquare, Copy, Trash2, RefreshCw, Star } from 'lucide-react';

const TestimonialGeneratorTool = () => {
  const [productName, setProductName] = useState('');
  const [industry, setIndustry] = useState('saas');
  const [tone, setTone] = useState('enthusiastic');
  const [count, setCount] = useState(3);
  const [testimonials, setTestimonials] = useState([]);
  const [copied, setCopied] = useState('');

  const industries = {
    saas: 'SaaS / Software',
    ecommerce: 'E-commerce',
    service: 'Professional Services',
    health: 'Health & Wellness',
    education: 'Education',
    finance: 'Finance'
  };

  const firstNames = [
    'Sarah', 'Michael', 'Jennifer', 'David', 'Emily', 'James', 'Jessica', 'Robert',
    'Ashley', 'John', 'Amanda', 'Christopher', 'Melissa', 'Daniel', 'Stephanie', 'Matthew'
  ];

  const lastNames = [
    'Smith', 'Johnson', 'Williams', 'Brown', 'Jones', 'Garcia', 'Miller', 'Davis',
    'Rodriguez', 'Martinez', 'Anderson', 'Taylor', 'Thomas', 'Moore', 'Jackson', 'Martin'
  ];

  const companies = [
    'TechCorp', 'StartupHub', 'Digital Solutions', 'Innovation Labs', 'Creative Agency',
    'Enterprise Co', 'Growth Partners', 'Nexus Inc', 'Peak Performance', 'Venture Works'
  ];

  const roles = [
    'CEO', 'Marketing Director', 'Product Manager', 'Founder', 'Operations Lead',
    'CTO', 'Head of Growth', 'Director of Sales', 'Business Owner', 'Team Lead'
  ];

  const templates = {
    enthusiastic: {
      saas: [
        `{product} has completely transformed how we work! We've seen a {metric}% increase in productivity since implementing it. Couldn't imagine going back.`,
        `Absolutely love {product}! The team adopted it within days and our {benefit} has never been better. Highly recommend!`,
        `Game changer! {product} solved problems we didn't even know we had. The support team is amazing too.`,
        `{product} is exactly what we needed. Easy to use, powerful features, and the results speak for themselves - {metric}% improvement in just {time}!`
      ],
      ecommerce: [
        `I was skeptical at first, but {product} exceeded all expectations! Fast shipping, great quality, and I've already ordered twice more.`,
        `{product} is a must-have! The quality is outstanding and customer service went above and beyond when I had questions.`,
        `So happy with my purchase! {product} is exactly as described and arrived even faster than expected. 5 stars!`
      ],
      service: [
        `Working with {product} has been incredible. They truly understand our needs and deliver results consistently.`,
        `{product} transformed our {benefit}. Professional, responsive, and exceeded our expectations. Highly recommend their services!`,
        `The team at {product} is phenomenal. They helped us achieve {metric}% growth in just {time}. Best investment we've made.`
      ]
    },
    professional: {
      saas: [
        `After evaluating several solutions, {product} stood out for its robust feature set and ease of implementation. We've achieved {metric}% ROI within {time}.`,
        `{product} provides excellent value for enterprise needs. The platform's reliability and scalability have been crucial for our operations.`,
        `Our team has benefited significantly from {product}. The analytics capabilities alone have improved our decision-making process considerably.`
      ],
      service: [
        `{product} delivered exceptional results on our project. Their methodical approach and expertise made a significant impact on our {benefit}.`,
        `We've partnered with {product} for {time} and consistently receive quality deliverables. Professional and reliable.`,
        `The consulting services from {product} provided valuable insights that directly improved our bottom line by {metric}%.`
      ]
    },
    casual: {
      saas: [
        `Honestly, {product} just works. No fuss, no confusion. Set it up and forget about it. Love it!`,
        `Finally a tool that doesn't require a PhD to use. {product} is simple, effective, and does exactly what it says.`,
        `Been using {product} for {time} now. It's become part of our daily routine. Solid choice!`
      ],
      ecommerce: [
        `Got my {product} last week - totally worth it! Already told all my friends about it.`,
        `This is my third order. {product} never disappoints. Quality stuff!`,
        `Quick delivery, nice packaging, great product. What more could you want? Thanks {product}!`
      ]
    }
  };

  const generateTestimonial = () => {
    const name = `${firstNames[Math.floor(Math.random() * firstNames.length)]} ${lastNames[Math.floor(Math.random() * lastNames.length)]}`;
    const company = companies[Math.floor(Math.random() * companies.length)];
    const role = roles[Math.floor(Math.random() * roles.length)];
    const rating = Math.floor(Math.random() * 2) + 4; // 4 or 5 stars
    
    const toneTemplates = templates[tone]?.[industry] || templates[tone]?.['saas'] || templates.enthusiastic.saas;
    let text = toneTemplates[Math.floor(Math.random() * toneTemplates.length)];
    
    const product = productName.trim() || 'this product';
    const metric = Math.floor(Math.random() * 50) + 20;
    const benefits = ['efficiency', 'productivity', 'workflow', 'team collaboration', 'customer satisfaction'];
    const times = ['2 weeks', '1 month', '3 months', '6 months'];
    
    text = text
      .replace(/{product}/g, product)
      .replace(/{metric}/g, metric)
      .replace(/{benefit}/g, benefits[Math.floor(Math.random() * benefits.length)])
      .replace(/{time}/g, times[Math.floor(Math.random() * times.length)]);
    
    return { name, company, role, rating, text };
  };

  const generateTestimonials = () => {
    const newTestimonials = [];
    for (let i = 0; i < count; i++) {
      newTestimonials.push(generateTestimonial());
    }
    setTestimonials(newTestimonials);
  };

  const copyTestimonial = (t, index) => {
    const text = `"${t.text}"\n\n— ${t.name}, ${t.role} at ${t.company}`;
    navigator.clipboard.writeText(text);
    setCopied(index);
    setTimeout(() => setCopied(''), 1500);
  };

  return (
    <div className="space-y-6">
      <div className="p-4 bg-blue-900/30 border border-blue-700 rounded-lg">
        <p className="text-sm text-blue-300">
          These are sample testimonials for design mockups and prototypes. 
          Always use real testimonials from actual customers in production.
        </p>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Product Name</label>
          <input
            type="text"
            value={productName}
            onChange={(e) => setProductName(e.target.value)}
            placeholder="Your product name"
            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Industry</label>
          <select
            value={industry}
            onChange={(e) => setIndustry(e.target.value)}
            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:border-blue-500"
          >
            {Object.entries(industries).map(([key, name]) => (
              <option key={key} value={key}>{name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Tone</label>
          <select
            value={tone}
            onChange={(e) => setTone(e.target.value)}
            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:border-blue-500"
          >
            <option value="enthusiastic">Enthusiastic</option>
            <option value="professional">Professional</option>
            <option value="casual">Casual</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Count</label>
          <input
            type="number"
            value={count}
            onChange={(e) => setCount(Math.max(1, Math.min(10, parseInt(e.target.value) || 3)))}
            min="1"
            max="10"
            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:border-blue-500"
          />
        </div>
      </div>

      <div className="flex gap-3">
        <button
          onClick={generateTestimonials}
          className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium"
        >
          <MessageSquare size={18} />
          Generate Testimonials
        </button>
        <button
          onClick={generateTestimonials}
          disabled={testimonials.length === 0}
          className="flex items-center gap-2 px-4 py-3 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-700 text-white rounded-lg transition-colors"
        >
          <RefreshCw size={18} />
        </button>
      </div>

      {testimonials.length > 0 && (
        <div className="grid gap-4">
          {testimonials.map((t, i) => (
            <div key={i} className="p-5 bg-gray-800 rounded-lg">
              <div className="flex items-start justify-between gap-4 mb-3">
                <div className="flex gap-1">
                  {[...Array(5)].map((_, s) => (
                    <Star
                      key={s}
                      size={16}
                      className={s < t.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-600'}
                    />
                  ))}
                </div>
                <button
                  onClick={() => copyTestimonial(t, i)}
                  className={`p-2 rounded transition-colors ${
                    copied === i ? 'bg-green-600' : 'bg-gray-700 hover:bg-gray-600'
                  }`}
                >
                  <Copy size={16} />
                </button>
              </div>
              <p className="text-gray-200 italic mb-4">"{t.text}"</p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-medium">
                  {t.name.split(' ').map(n => n[0]).join('')}
                </div>
                <div>
                  <div className="font-medium text-white">{t.name}</div>
                  <div className="text-sm text-gray-400">{t.role} at {t.company}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <button
        onClick={() => setTestimonials([])}
        disabled={testimonials.length === 0}
        className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
      >
        <Trash2 size={16} />
        Clear
      </button>
    </div>
  );
};

export default TestimonialGeneratorTool;
